import { DeliveryStatus } from 'config/constants/order.constants';
import { OrderTranscription } from 'config/constants/transcription.constant';
import { store } from 'store/index';
import { OrderSlice } from 'store/slices/order.slice';

const PURCHASE_STATUS_COLOR: Record<DeliveryStatus, string> = {
  [DeliveryStatus.DELIVERED]: 'success',
  [DeliveryStatus.PENDING]: 'warning',
  [DeliveryStatus.NO_APPLY]: 'default',
};

const PURCHASE_STATUS_TEXT: Record<DeliveryStatus, string> = {
  [DeliveryStatus.DELIVERED]: OrderTranscription.PURCHASE_STATUS_DELIVERED,
  [DeliveryStatus.PENDING]: OrderTranscription.PURCHASE_STATUS_PENDING,
  [DeliveryStatus.NO_APPLY]: OrderTranscription.PURCHASE_STATUS_NO_APPLY,
};

const getPurchaseStatusColor = (status?: DeliveryStatus): string => PURCHASE_STATUS_COLOR[status ?? DeliveryStatus.NO_APPLY] ?? 'default';

const getPurchaseStatusText = (status?: DeliveryStatus): string =>
  PURCHASE_STATUS_TEXT[status ?? DeliveryStatus.NO_APPLY] ?? OrderTranscription.PURCHASE_STATUS_NO_APPLY;

const getCrypto = (): Crypto => {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) {
    throw new Error('No se pudo generar un identificador seguro para la compra');
  }
  return cryptoApi;
};

const formatUuid = (bytes: Uint8Array): string => {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const generateUuid = (): string => {
  const cryptoApi = getCrypto();
  if (typeof cryptoApi.randomUUID === 'function') {
    try {
      return cryptoApi.randomUUID();
    } catch {
      // randomUUID requires a secure context in some browsers.
    }
  }

  return formatUuid(cryptoApi.getRandomValues(new Uint8Array(16)));
};

const generateOrderEntryCode = (): string => {
  const randomString = generateUuid().replace(/-/g, '').slice(0, 20);
  return `OC-${randomString.toUpperCase()}`;
};

const rotateIdempotencyKey = (): string => {
  const key = generateUuid();
  store.dispatch(OrderSlice.refreshIdempotencyKey(key));
  return key;
};

const ensureIdempotencyKey = (): string => {
  const currentKey = store.getState().order.idempotencyKey;
  return currentKey || rotateIdempotencyKey();
};

const openPdfFromUrl = async (url: string, fileName = 'boleta.pdf'): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.status}`);
  }

  const blob = await response.blob();
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
  const objectUrl = URL.createObjectURL(pdfBlob);
  const opened = window.open(objectUrl, '_blank', 'noopener,noreferrer');

  if (!opened) {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const OrderUtils = {
  generateOrderEntryCode,
  ensureIdempotencyKey,
  rotateIdempotencyKey,
  openPdfFromUrl,
  getPurchaseStatusColor,
  getPurchaseStatusText,
};

export default OrderUtils;
